import React from 'react';
import { useIntl } from 'react-intl';

import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { queryClient } from 'src/lib/react-query';
import { toast } from 'src/hooks/useToast';
import { useUpdateVoucherType } from 'src/features/voucherTypes/api/useUpdateVoucherType';
import { VoucherType } from 'src/features/voucherTypes/utils/types';
import { LIST_VOUCHER_TYPES_RESOURCE } from 'src/features/voucherTypes/api/useListVoucherTypes';
import { VOUCHER_TYPE_RESOURCE } from 'src/features/voucherTypes/api/useGetVoucherType';
import { voucherTypesMessages } from 'src/features/voucherTypes/utils/messages';
import VoucherTypeFormModal from 'src/features/voucherTypes/components/forms/VoucherTypeFormModal';

interface UpdateVoucherTypeFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedVoucherType: VoucherType;
    children?: React.ReactNode;
}

function UpdateVoucherTypeFormModal<TData>({
    selectedVoucherType,
    className,
    children,
}: UpdateVoucherTypeFormModalProps<TData>) {
    const { formatMessage } = useIntl();

    const { mutate, isPending } = useUpdateVoucherType({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_VOUCHER_TYPES_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [VOUCHER_TYPE_RESOURCE] });
            toast({
                title: formatMessage(voucherTypesMessages.updateVoucherTypeSuccess),
                description: formatMessage(voucherTypesMessages.updateVoucherTypeSuccessDescription),
            });
        },
    });

    return (
        <VoucherTypeFormModal
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutate({ voucherTypeId: selectedVoucherType.id, ...values }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, {
                entry: formatMessage(voucherTypesMessages.voucherType).toLowerCase(),
            })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(voucherTypesMessages.editVoucherTypeSubtitle)}
            selectedVoucherType={selectedVoucherType}
        >
            {children}
        </VoucherTypeFormModal>
    );
}

export default UpdateVoucherTypeFormModal;
